import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { slugify } from '../../utils';
import {
  getTrack,
  updateTrack,
  deleteTrack,
  uploadTrack as uploadTrackFile,
  getTrackPublicUrl,
  type Track,
} from '../../services/api/albums';

export default function StandaloneTrackEditorPage() {
  const { trackId } = useParams<{ trackId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [track, setTrack] = useState<Track | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [replaceFile, setReplaceFile] = useState<File | null>(null);

  useEffect(() => {
    if (!trackId) return;
    getTrack(trackId)
      .then((tr) => {
        setTrack(tr);
        setTitle(tr.title_en);
        if (tr.album_id) {
          navigate(`/admin/music/albums/${tr.album_id}`, { replace: true });
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [trackId, navigate]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!trackId || !track) return;
    setSaving(true);
    try {
      await updateTrack(trackId, { title_en: title });
      if (replaceFile) {
        const ext = replaceFile.name.split('.').pop()?.toLowerCase() || 'mp3';
        const path = `singles/${slugify(title) || 'track'}.${ext}`;
        await uploadTrackFile(path, replaceFile);
        await updateTrack(trackId, { file_storage_path: path });
      }
      navigate('/admin/music', { replace: true });
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!trackId || !confirm(t('admin.deleteTrackConfirm'))) return;
    await deleteTrack(trackId);
    navigate('/admin/music', { replace: true });
  }

  if (loading || !track) return <p className="text-[var(--color-text-muted)]">{t('common.loading')}</p>;
  if (track.album_id) return null;

  return (
    <div className="admin-page">
      <h1 className="admin-page__title">{t('admin.editStandaloneTrack')}</h1>
      <p className="admin-section-hint">{t('admin.editStandaloneTrackHint')}</p>
      <form onSubmit={handleSave} className="admin-standalone-track-form">
        <div className="admin-form-group">
          <label>{t('admin.trackTitle')}</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full rounded border border-[var(--color-border)] bg-black px-3 py-2 text-[var(--color-text)]"
          />
        </div>
        {track.file_storage_path && !replaceFile && (
          <div className="admin-form-group">
            <label>{t('admin.currentFile')}</label>
            <p className="admin-standalone-track__file">
              <a href={getTrackPublicUrl(track.file_storage_path)} target="_blank" rel="noopener noreferrer">
                {track.file_storage_path}
              </a>
            </p>
          </div>
        )}
        <div className="admin-form-group">
          <label>{t('admin.replaceFile')}</label>
          <div className="admin-file-upload">
            <label className="admin-file-upload__label">
              <input
                type="file"
                accept="audio/mpeg,.mp3,audio/*"
                onChange={(e) => setReplaceFile(e.target.files?.[0] ?? null)}
                className="admin-file-upload__input"
              />
              <span className="admin-file-upload__text">
                {replaceFile ? replaceFile.name : t('admin.chooseMp3')}
              </span>
            </label>
            <span className="admin-file-upload__hint">{t('admin.replaceFileHint')}</span>
          </div>
        </div>
        <div className="admin-save-row">
          <button type="submit" disabled={saving} className="admin-btn admin-btn--primary">
            {saving ? t('admin.savingAlbum') : t('common.save')}
          </button>
          <button type="button" onClick={handleDelete} className="admin-btn admin-btn--danger">
            {t('admin.removeTrack')}
          </button>
          <Link to="/admin/music" className="admin-btn">
            {t('common.cancel')}
          </Link>
        </div>
      </form>
    </div>
  );
}
